import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "../../api/api";
import { toast } from "react-toastify";
import SEOSection from "../../components/SEOSection/SEOSection";

const fieldConfigs = {
  author: {
    type: "author",
    label: "Author",
    placeholder: "Enter author name",
    isTextarea: false,
  },
  title: {
    type: "title",
    label: "Title",
    placeholder: "Enter title",
    isTextarea: false,
  },
  description: {
    type: "description",
    label: "Description",
    placeholder: "Enter description",
    isTextarea: true,
  },
  image: {
    type: "image",
    label: "Image",
    placeholder: "Enter image URL",
    isTextarea: false,
  },
  video: {
    type: "video",
    label: "Video",
    placeholder: "Enter video URL",
    isTextarea: false,
  },
  icon: {
    type: "icon",
    label: "Icon",
    placeholder: "Enter icon URL",
    isTextarea: false,
  },
  keyFeatures: {
    type: "keyFeatures",
    label: "Key Features",
    placeholder: "Enter key features",
    isTextarea: true,
  },
  faq: {
    type: "faq",
    label: "FAQ",
    placeholder: "Enter question",
    isTextarea: false,
  },
  tag: {
    type: "tag",
    label: "Tag",
    placeholder: "Enter tag",
    isTextarea: false,
  },
};

const FieldInput = ({
  id,
  type,
  label,
  index,
  placeholder,
  isTextarea,
  removeField,
  dragListeners,
  depth,
  data,
}) => (
  <div
    className={`mb-4 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm ${
      depth > 0 ? "border-primary-500" : ""
    }`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center">
        <div {...dragListeners} className="cursor-grab mr-2 text-gray-500">
          ≡
        </div>
        <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
          {label} {index !== null ? index + 1 : ""}
        </span>
      </div>
      <button
        type="button"
        onClick={removeField}
        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
      >
        Remove
      </button>
    </div>
    {type === "faq" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Question
        </label>
        <input
          type="text"
          id={`question-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.question || ""}
          required
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Answer
        </label>
        <textarea
          id={`answer-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter answer"
          defaultValue={data?.answer || ""}
          required
        />
      </>
    ) : type === "image" || type === "video" || type === "icon" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          URL (optional)
        </label>
        <input
          type="url"
          id={`url-${type}-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={`${placeholder} (optional)`}
          defaultValue={data && !data.startsWith("/uploads/") ? data : ""}
        />
        <div className="mt-3">
          <label
            htmlFor={`file-${type}-${id}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Or Upload File (optional)
          </label>
          <input
            type="file"
            accept={type === "video" ? "video/*" : "image/*"}
            id={`file-${type}-${id}`}
            name={`file-${type}-${id}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200"
          />
        </div>
        {data && data.startsWith("/uploads/") && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Preview
            </label>
            {type === "video" ? (
              <video
                src={`${process.env.REACT_APP_BACKEND_API_URL}${data}`}
                controls
                width="200"
                onError={(e) => (e.target.src = "/fallback-video.mp4")}
              />
            ) : (
              <img
                src={`${process.env.REACT_APP_BACKEND_API_URL}${data}`}
                alt="Preview"
                style={{ maxWidth: "200px" }}
                onError={(e) => (e.target.src = "/fallback-image.png")}
              />
            )}
          </div>
        )}
      </>
    ) : isTextarea ? (
      <textarea
        id={`${type}-${id}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
        placeholder={placeholder}
        defaultValue={data || ""}
        required
      />
    ) : (
      <input
        type="text"
        id={`${type}-${id}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
        placeholder={placeholder}
        defaultValue={data || ""}
        required
      />
    )}
  </div>
);

const TreeItem = ({
  item,
  depth,
  removeField,
  allFieldsItems,
  activeId,
  projected,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const isMeta = item.type === "metaTitle" || item.type === "metaDescription";
  const sameTypeFields = allFieldsItems.filter((f) => f.type === item.type);
  const index = isMeta
    ? null
    : sameTypeFields.findIndex((f) => f.id === item.id);
  const currentDepth =
    activeId === item.id && projected ? projected.depth : depth;

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="mb-2">
      <FieldInput
        id={item.id}
        type={item.type}
        label={item.label}
        index={index}
        placeholder={item.placeholder}
        isTextarea={item.isTextarea}
        removeField={() => removeField(item.id)}
        dragListeners={listeners}
        depth={currentDepth}
        data={item.data}
      />
      {Array.isArray(item.children) && item.children.length > 0 && (
        <div className="ml-6">
          <SortableContext
            id={`sortable-${item.id}`}
            items={item.children.map((child) => child.id)}
            strategy={verticalListSortingStrategy}
          >
            {item.children.map((child) => (
              <TreeItem
                key={child.id}
                item={child}
                depth={depth + 1}
                removeField={removeField}
                allFieldsItems={allFieldsItems}
                activeId={activeId}
                projected={projected}
              />
            ))}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

function flattenTree(items, parent = null, depth = 0) {
  return items.reduce((acc, item) => {
    const newItem = { ...item, parent, depth };
    acc.push(newItem);
    if (item.children && item.children.length > 0) {
      acc.push(...flattenTree(item.children, item.id, depth + 1));
    }
    return acc;
  }, []);
}

function buildTree(flattenedItems) {
  const idMapping = flattenedItems.reduce((acc, item) => {
    acc[item.id] = {
      id: item.id,
      type: item.type,
      label: item.label,
      placeholder: item.placeholder,
      isTextarea: item.isTextarea,
      data: item.data,
      children: [],
    };
    return acc;
  }, {});

  const root = [];

  flattenedItems.forEach((item) => {
    const node = idMapping[item.id];
    if (item.parent === null) {
      root.push(node);
    } else {
      const parentNode = idMapping[item.parent];
      if (parentNode) {
        parentNode.children.push(node);
      }
    }
  });

  return root;
}

function getProjection(
  items,
  activeId,
  overId,
  dragOffset,
  indentationWidth = 20
) {
  const overItemIndex = items.findIndex((i) => i.id === overId);
  const activeItemIndex = items.findIndex((i) => i.id === activeId);

  if (overItemIndex === -1 || activeItemIndex === -1) return null;

  const activeItem = items[activeItemIndex];
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];

  const dragDepth = Math.round(dragOffset / indentationWidth);
  const tentativeDepth = Math.max(0, activeItem.depth + dragDepth);

  const maxDepth = getMaxDepth(previousItem);
  const minDepth = getMinDepth(nextItem);

  let depth = Math.max(minDepth, Math.min(maxDepth, tentativeDepth));

  let parentId;
  if (depth === 0) {
    parentId = null;
  } else {
    parentId = getParentId(previousItem, depth, newItems);
  }

  return { depth, maxDepth, minDepth, parentId };
}

function getMaxDepth(previousItem) {
  return previousItem ? previousItem.depth + 1 : 0;
}

function getMinDepth(nextItem) {
  return nextItem ? nextItem.depth : 0;
}

function getParentId(previousItem, depth, items) {
  if (!previousItem) return null;

  let current = previousItem;
  let currentIndex = items.findIndex((i) => i.id === current.id);

  while (current && current.depth >= depth && currentIndex > 0) {
    currentIndex -= 1;
    current = items[currentIndex];
  }

  return current ? current.id : null;
}

function removeFromTree(items, id) {
  return items.filter((item) => {
    if (item.id === id) return false;
    if (item.children) {
      item.children = removeFromTree(item.children, id);
    }
    return true;
  });
}

function getAllFields(items) {
  return items.reduce(
    (acc, item) => [...acc, item, ...getAllFields(item.children || [])],
    []
  );
}

function hydrateContent(content, generateId) {
  if (!Array.isArray(content)) return [];
  return content
    .map((node) => {
      const config = fieldConfigs[node.type];
      if (!config) return null;
      const data =
        node.type === "faq"
          ? node.data || { question: "", answer: "" }
          : node.data || "";
      return {
        id: generateId(),
        ...config,
        data,
        children: node.children
          ? hydrateContent(node.children, generateId)
          : [],
      };
    })
    .filter(Boolean);
}

export default function AddBlog({
  isEdit = false,
  blogId = null,
  onSuccess = () => {},
}) {
  const generateId = () =>
    crypto.randomUUID?.() || Math.random().toString(36).substr(2, 9);
  const [fields, setFields] = useState({ allFields: [] });
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [projected, setProjected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    ogImage: "",
  });

  const handleSeoChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prev) => ({ ...prev, [name]: value }));
  };

  const flattenedFields = useMemo(
    () => flattenTree(fields.allFields),
    [fields.allFields]
  );
  const allFieldsItems = useMemo(
    () => getAllFields(fields.allFields),
    [fields.allFields]
  );

  useEffect(() => {
    if (isEdit && blogId) {
      fetchBlogById(blogId);
    } else {
      const defaultFields = [
        { id: generateId(), ...fieldConfigs.author, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.title, data: "", children: [] },
        {
          id: generateId(),
          ...fieldConfigs.description,
          data: "",
          children: [],
        },
        { id: generateId(), ...fieldConfigs.image, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.video, data: "", children: [] },
        { id: generateId(), ...fieldConfigs.icon, data: "", children: [] },
        {
          id: generateId(),
          ...fieldConfigs.keyFeatures,
          data: "",
          children: [],
        },
          {
            id: generateId(),
            ...fieldConfigs.faq,
            data: { question: "", answer: "" },
            children: [],
          },
          { id: generateId(), ...fieldConfigs.tag, data: "", children: [] },
        ];
      setFields({ allFields: defaultFields });
    }
    fetchCategories();
  }, [isEdit, blogId]);

  const fetchBlogById = async (id) => {
    setLoading(true);
    try {
      const resp = await axios.get(`/api/blog/${id}`);
      const blogObj = resp?.data?.data || resp?.data;
      if (blogObj && blogObj.content) {
        const hydrated = hydrateContent(blogObj.content, generateId);
        setFields({ allFields: hydrated });
        setSelectedCategory(blogObj.category || "");
        setSeoData({
          metaTitle: blogObj.metaTitle || "",
          metaDescription: blogObj.metaDescription || "",
          metaKeywords: blogObj.metaKeywords || "",
          ogImage: blogObj.ogImage || "",
        });
      } else {
        toast.error("Invalid blog data to edit");
      }
    } catch (err) {
      console.error("Error fetching blog:", err);
      toast.error("Failed to load blog data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const resp = await axios.get("/api/blog/category/categories");
      setCategories(resp.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories");
    }
  };

  const removeField = (id) => {
    setFields((prev) => ({
      allFields: removeFromTree(prev.allFields, id),
    }));
  };

  const areAllFieldsRemoved = () => fields.allFields.length === 0;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
    setOverId(active.id);
    setOffsetLeft(0);
  };

  const handleDragMove = ({ delta, over }) => {
    setOffsetLeft(delta.x);
    if (over) {
      setOverId(over.id);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (projected && over) {
      const { depth, parentId } = projected;
      const cloned = [...flattenedFields];
      const overIndex = cloned.findIndex((i) => i.id === over.id);
      const activeIndex = cloned.findIndex((i) => i.id === active.id);
      const activeTreeItem = cloned[activeIndex];
      cloned[activeIndex] = { ...activeTreeItem, depth, parent: parentId };
      const newItems = arrayMove(cloned, activeIndex, overIndex);
      setFields((prev) => ({
        allFields: buildTree(newItems),
      }));
    }
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverId(null);
    setProjected(null);
    setOffsetLeft(0);
  };

  useEffect(() => {
    if (activeId && overId) {
      setProjected(
        getProjection(flattenedFields, activeId, overId, offsetLeft, 20)
      );
    } else {
      setProjected(null);
    }
  }, [activeId, overId, offsetLeft, flattenedFields]);

  const addField = (fieldType) => {
    setFields((prev) => {
      const config = fieldConfigs[fieldType];
      if (!config) return prev;
      if (["metaTitle", "metaDescription"].includes(fieldType)) {
        if (
          getAllFields(prev.allFields).some((item) => item.type === fieldType)
        ) {
          toast.warn(`Only one ${config.label} is allowed`);
          return prev;
        }
      }
      const newId = generateId();
      const newField = {
        id: newId,
        ...config,
        data: fieldType === "faq" ? { question: "", answer: "" } : "",
        children: [],
      };
      return { allFields: [...prev.allFields, newField] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const contentCopy = JSON.parse(JSON.stringify(fields.allFields));
    const formData = new FormData();

    const traverseSetValues = (nodes) => {
      return nodes.map((node) => {
        const newNode = { type: node.type, id: node.id };
        if (
          [
            "title",
            "author",
            "description",
            "keyFeatures",
            "metaTitle",
            "metaDescription",
            "tag",
          ].includes(node.type)
        ) {
          const el = document.getElementById(`${node.type}-${node.id}`);
          if (!el) {
            throw new Error(`Missing element for ${node.type}-${node.id}`);
          }
          const value = el.value?.trim();
          if (!value) {
            toast.error(`${node.label} cannot be empty`);
            throw new Error(`${node.label} is required`);
          }
          newNode.data = value;
        } else if (node.type === "faq") {
          const qEl = document.getElementById(`question-${node.id}`);
          const aEl = document.getElementById(`answer-${node.id}`);
          const question = qEl?.value?.trim() || "";
          const answer = aEl?.value?.trim() || "";
          if (!question || !answer) {
            toast.error("All FAQs must have a question and answer");
            throw new Error("Invalid FAQ data");
          }
          newNode.data = { question, answer };
        } else if (["image", "video", "icon"].includes(node.type)) {
          const urlEl = document.getElementById(`url-${node.type}-${node.id}`);
          const url = urlEl?.value?.trim();
          const fileInput = document.getElementById(
            `file-${node.type}-${node.id}`
          );
          if (url) {
            newNode.data = url;
          } else if (fileInput?.files?.[0]) {
            formData.append(`${node.type}-${node.id}`, fileInput.files[0]);
            newNode.data = "";
          } else {
            newNode.data = node.data || "";
          }
        }
        if (node.children?.length) {
          newNode.children = traverseSetValues(node.children);
        }
        return newNode;
      });
    };

    let contentArray;
    try {
      contentArray = traverseSetValues(contentCopy);
    } catch (err) {
      console.error("Validation error:", err);
      return;
    }

    formData.append("content", JSON.stringify(contentArray));
    formData.append("category", selectedCategory);
    formData.append("metaTitle", seoData.metaTitle);
    formData.append("metaDescription", seoData.metaDescription);
    formData.append("metaKeywords", seoData.metaKeywords);
    formData.append("ogImage", seoData.ogImage);

    try {
      let resp;
      if (isEdit) {
        resp = await axios.put(`/api/blog/${blogId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Blog updated successfully!");
      } else {
        resp = await axios.post("/api/blog/add-blog", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Blog added successfully!");
      }
      setFields({ allFields: [] });
      setSelectedCategory("");
      onSuccess();
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(
        `Failed to ${isEdit ? "update" : "add"} blog: ${err.message}`
      );
    }
  };

  const activeItem = activeId
    ? flattenedFields.find((i) => i.id === activeId)
    : null;

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24 duration-300">
        <div className="xl:w-full min-h-[calc(100vh-152px)] relative pb-14">
          <div className="grid md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12 gap-6 mb-6">
            <div className="sm:col-span-12 md:col-span-12 lg:col-span-8 xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg w-full relative mb-6 shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 dark:text-gray-300">
                  <h4 className="font-semibold text-xl flex-1 self-center mb-2 md:mb-0">
                    {isEdit ? "Edit Blog" : "Add Blog"}
                  </h4>
                </div>
                <form onSubmit={handleSubmit} className="flex-auto p-6">
                  {loading ? (
                    <div className="space-y-4">
                      {Array(5)
                        .fill()
                        .map((_, idx) => (
                          <div
                            key={idx}
                            className="animate-pulse bg-gray-200 dark:bg-gray-700 h-16 rounded-md"
                          ></div>
                        ))}
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCorners}
                      onDragStart={handleDragStart}
                      onDragMove={handleDragMove}
                      onDragEnd={handleDragEnd}
                      onDragCancel={handleDragCancel}
                    >
                      <SortableContext
                        id="root"
                        items={fields.allFields.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {areAllFieldsRemoved() ? (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                            No fields have been added. Please add fields using
                            the buttons below.
                          </p>
                        ) : (
                          fields.allFields.map((item) => (
                            <TreeItem
                              key={item.id}
                              item={item}
                              depth={0}
                              removeField={removeField}
                              allFieldsItems={allFieldsItems}
                              activeId={activeId}
                              projected={projected}
                            />
                          ))
                        )}
                      </SortableContext>
                      <DragOverlay>
                        {activeId && activeItem ? (
                          <FieldInput
                            id={activeItem.id}
                            type={activeItem.id}
                            label={activeItem.label}
                            index={null}
                            placeholder={activeItem.placeholder}
                            isTextarea={activeItem.isTextarea}
                            removeField={() => {}}
                            dragListeners={{}}
                            depth={
                              projected ? projected.depth : activeItem.depth
                            }
                            data={activeItem.data}
                          />
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}
                  
                  {/* <SEOSection formData={seoData} onChange={handleSeoChange} /> */}

                  <div className="mb-4 mt-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                    >
                      <option value="">Select Category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <SEOSection formData={seoData} onChange={handleSeoChange} />

                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                      disabled={areAllFieldsRemoved() || loading}
                    >
                      {isEdit ? "Update" : "Submit"}
                    </button>
                    <button
                      type="button"
                      className="ml-3 px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      onClick={() => {
                        setFields({ allFields: [] });
                        setSelectedCategory("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
                <div className="p-6 pt-0">
                  <div className="mt-8">
                    <h5 className="font-semibold text-base text-gray-700 dark:text-gray-300 mb-3">
                      Add Fields
                    </h5>
                    <div className="flex flex-wrap gap-3">
                      {Object.keys(fieldConfigs).map((fieldType) => (
                        <button
                          key={fieldType}
                          onClick={() => addField(fieldType)}
                          className="px-4 py-2 bg-transparent border border-primary-500 text-primary-500 rounded-md hover:bg-primary-500 hover:text-white"
                        >
                          Add {fieldConfigs[fieldType].label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}